// Distribution-domain list filters: merchandisers (PD-13923) and sensor
// devices (PD-13926). Both lists are backed by dedicated entities/endpoints
// (/api/merchandisers, /api/sensor-devices) rather than the generic object
// list, so their filters live here and are referenced by `activeFilters` in
// the corresponding list-builder configs.

import { createKeywordFilter, createStatusFilter } from './factories';
import type { FilterRegistry } from './types';

export const distributionFilters: FilterRegistry = {
	// --- Merchandiser list ---
	merchandiserSearch: {
		...createKeywordFilter({
			label: 'Search',
			placeholder: 'Search merchandisers...',
			objectTable: 'merchandiser',
			listKeys: ['merchandiser'],
		}),
		queryParamName: 'search',
	},

	merchandiserStatus: {
		...createStatusFilter({
			label: 'Status',
			objectTable: 'merchandiser',
			fieldName: 'status',
			listKeys: ['merchandiser'],
			options: [
				{ id: 'active', label: 'Active' },
				{ id: 'inactive', label: 'Inactive' },
				{ id: 'maintenance', label: 'Maintenance' },
				{ id: 'offline', label: 'Offline' },
			],
		}),
		queryParamName: 'merchandiserStatus',
	},

	// Fill level (PD-12625) — the headline refill-planning filter on the
	// merchandiser map. Derived from the inventory rollup (stockLevel), so it
	// has no queryMapping; the map endpoint filters on the computed value.
	merchandiserFillLevel: {
		type: 'multi-select',
		label: 'Fill Level',
		placeholder: 'Select fill level...',
		colorScheme: 'orange',
		searchable: false,
		loadOnFocus: false,
		options: [
			{ id: 'Empty', label: 'Empty' },
			{ id: 'Low', label: 'Low' },
			{ id: 'Medium', label: 'Medium' },
			{ id: 'Full', label: 'Full' },
		],
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'fillLevel',
		multiple: true,
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['merchandiser'],
	},

	// High temperature (PD-13938) — derived quick-filter over the temperature
	// bubbled up from the attached sensor device
	// (merchandiser.currentTemperature). Single-option: when set, the
	// merchandisers backend reads the `highTemp` param and applies
	// currentTemperature >= MERCHANDISER_HIGH_TEMP_THRESHOLD (the
	// team-tunable constant in @supply-mover-2-0/shared). No queryMapping —
	// mirrors deviceFilter below.
	merchandiserHighTemp: {
		type: 'single-select',
		label: 'Temperature',
		placeholder: 'Any temperature',
		colorScheme: 'red',
		searchable: false,
		loadOnFocus: false,
		allowClear: true,
		options: [{ id: 'high', label: 'High Temp' }],
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'highTemp',
		multiple: false,
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['merchandiser'],
	},

	// --- Sensor device list ---
	sensorDeviceSearch: {
		...createKeywordFilter({
			label: 'Search',
			placeholder: 'Device ID, name, merchandiser...',
			objectTable: 'sensor_device',
			listKeys: ['sensor_device'],
		}),
		queryParamName: 'search',
	},

	// Derived quick-filter — NOT a single column. "Needs Attention" is a
	// compound health condition (offline/error/stale/low-battery), "Has
	// Orphans" is a post-count comparison (assigned < total), and
	// "Unattached" is merchandiserId IS NULL. So there is no queryMapping:
	// the sensor-devices backend reads the `deviceFilter` param and applies
	// the condition itself (mirrors vehicleTripStatus).
	deviceFilter: {
		type: 'single-select',
		label: 'Show',
		placeholder: 'All devices',
		colorScheme: 'purple',
		searchable: false,
		loadOnFocus: false,
		allowClear: true,
		options: [
			{ id: 'needs_attention', label: 'Needs Attention' },
			{ id: 'has_orphans', label: 'Has Orphans' },
			{ id: 'unattached', label: 'Unattached' },
		],
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'deviceFilter',
		multiple: false,
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['sensor_device'],
	},
};
